import { takeLatest, call, put } from 'redux-saga/effects';
import {
    getExternalReferrersSuccess,
    getExternalReferrersFailure,
    getExternalReferrerSuccess,
    getExternalReferrerFailure,
    createExternalReferrerSuccess,
    createExternalReferrerFailure,
    updateExternalReferrerSuccess,
    updateExternalReferrerFailure,
    deleteExternalReferrerSuccess,
    deleteExternalReferrerFailure,
    assignOrderSuccess,
    assignOrderFailure,
    revokeOrderSuccess,
    revokeOrderFailure,
    getAssignedOrdersSuccess,
    getAssignedOrdersFailure,
    searchOrdersSuccess,
    searchOrdersFailure,
    getMyOrdersSuccess,
    getMyOrdersFailure,
    getOrderDetailSuccess,
    getOrderDetailFailure,
    getOrderReportSuccess,
    getOrderReportFailure,
} from './slice';
import requestHandler from '../../../utils/requestHandler';
import { logoutRequest } from '../auth/authSlice';

const getAuthHeaders = () => {
    const authToken = localStorage.getItem("item");
    const currentBranchRaw = localStorage.getItem("currentBranch");
    const headers = {
        "Authorization": authToken,
        "Content-Type": "application/json",
    };

    // Parse branch from localStorage (stored as JSON object)
    if (currentBranchRaw) {
        try {
            const branchData = JSON.parse(currentBranchRaw);
            if (branchData?.id) {
                headers["X-Current-Branch"] = branchData.id;
            }
        } catch (e) {
            // Invalid JSON in currentBranch, skip header
        }
    }

    return headers;
};

// List external referrers
function* getExternalReferrers(action) {
    try {
        const authToken = localStorage.getItem("item");
        if (!authToken) {
            yield put(logoutRequest());
            return;
        }
        const { page = 1, limit = 10, searchQuery = "" } = action.payload || {};
        const response = yield call(
            requestHandler,
            `${process.env.REACT_APP_SERVER_API}api/external-referrers?page=${page}&limit=${limit}&searchQuery=${searchQuery}`,
            { method: "GET", headers: getAuthHeaders() }
        );
        yield put(getExternalReferrersSuccess(response));
    } catch (error) {
        yield put(getExternalReferrersFailure(error?.response?.message || error));
    }
}

// Get single external referrer
function* getExternalReferrer(action) {
    try {
        const authToken = localStorage.getItem("item");
        if (!authToken) {
            yield put(logoutRequest());
            return;
        }
        const response = yield call(
            requestHandler,
            `${process.env.REACT_APP_SERVER_API}api/external-referrer/${action.payload}`,
            { method: "GET", headers: getAuthHeaders() }
        );
        yield put(getExternalReferrerSuccess(response));
    } catch (error) {
        yield put(getExternalReferrerFailure(error?.response?.message || error));
    }
}

// Create external referrer
function* createExternalReferrer(action) {
    try {
        const authToken = localStorage.getItem("item");
        if (!authToken) {
            yield put(logoutRequest());
            return;
        }
        const response = yield call(
            requestHandler,
            `${process.env.REACT_APP_SERVER_API}api/external-referrer`,
            {
                method: "POST",
                headers: getAuthHeaders(),
                body: JSON.stringify(action.payload)
            }
        );
        yield put(createExternalReferrerSuccess(response));
    } catch (error) {
        yield put(createExternalReferrerFailure(error?.response?.message || error));
    }
}

// Update external referrer
function* updateExternalReferrer(action) {
    try {
        const authToken = localStorage.getItem("item");
        if (!authToken) {
            yield put(logoutRequest());
            return;
        }
        const { id, ...data } = action.payload;
        const response = yield call(
            requestHandler,
            `${process.env.REACT_APP_SERVER_API}api/external-referrer/${id}`,
            {
                method: "PUT",
                headers: getAuthHeaders(),
                body: JSON.stringify(data)
            }
        );
        yield put(updateExternalReferrerSuccess(response));
    } catch (error) {
        yield put(updateExternalReferrerFailure(error?.response?.message || error));
    }
}

// Delete external referrer
function* deleteExternalReferrer(action) {
    try {
        const authToken = localStorage.getItem("item");
        if (!authToken) {
            yield put(logoutRequest());
            return;
        }
        const response = yield call(
            requestHandler,
            `${process.env.REACT_APP_SERVER_API}api/external-referrer/${action.payload}`,
            { method: "DELETE", headers: getAuthHeaders() }
        );
        yield put(deleteExternalReferrerSuccess(response));
    } catch (error) {
        yield put(deleteExternalReferrerFailure(error?.response?.message || error));
    }
}

// Assign order to external referrer
function* assignOrder(action) {
    try {
        const authToken = localStorage.getItem("item");
        if (!authToken) {
            yield put(logoutRequest());
            return;
        }
        const { externalReferrerId, ...orderData } = action.payload;
        const response = yield call(
            requestHandler,
            `${process.env.REACT_APP_SERVER_API}api/external-referrer/${externalReferrerId}/assign-order`,
            {
                method: "POST",
                headers: getAuthHeaders(),
                body: JSON.stringify(orderData)
            }
        );
        yield put(assignOrderSuccess(response));
    } catch (error) {
        yield put(assignOrderFailure(error?.response?.message || error));
    }
}

// Revoke order access
function* revokeOrder(action) {
    try {
        const authToken = localStorage.getItem("item");
        if (!authToken) {
            yield put(logoutRequest());
            return;
        }
        const { externalReferrerId, orderId, reason } = action.payload;
        const response = yield call(
            requestHandler,
            `${process.env.REACT_APP_SERVER_API}api/external-referrer/${externalReferrerId}/order/${orderId}`,
            {
                method: "DELETE",
                headers: getAuthHeaders(),
                body: JSON.stringify({ reason })
            }
        );
        yield put(revokeOrderSuccess(response));
    } catch (error) {
        yield put(revokeOrderFailure(error?.response?.message || error));
    }
}

// Get assigned orders for external referrer
function* getAssignedOrders(action) {
    try {
        const authToken = localStorage.getItem("item");
        if (!authToken) {
            yield put(logoutRequest());
            return;
        }
        const { id, page = 1, limit = 10 } = action.payload;
        const response = yield call(
            requestHandler,
            `${process.env.REACT_APP_SERVER_API}api/external-referrer/${id}/orders?page=${page}&limit=${limit}`,
            { method: "GET", headers: getAuthHeaders() }
        );
        yield put(getAssignedOrdersSuccess(response));
    } catch (error) {
        yield put(getAssignedOrdersFailure(error?.response?.message || error));
    }
}

// Search orders for assignment
function* searchOrders(action) {
    try {
        const authToken = localStorage.getItem("item");
        if (!authToken) {
            yield put(logoutRequest());
            return;
        }
        const response = yield call(
            requestHandler,
            `${process.env.REACT_APP_SERVER_API}api/external-referrer/search-orders?query=${encodeURIComponent(action.payload)}`,
            { method: "GET", headers: getAuthHeaders() }
        );
        yield put(searchOrdersSuccess(response));
    } catch (error) {
        yield put(searchOrdersFailure(error?.response?.message || error));
    }
}

// ==================== External Portal Sagas ====================

// Get my assigned orders (external user)
function* getMyOrders(action) {
    try {
        const authToken = localStorage.getItem("item");
        if (!authToken) {
            yield put(logoutRequest());
            return;
        }
        const { page = 1, limit = 10, search = '' } = action.payload || {};
        let url = `${process.env.REACT_APP_SERVER_API}api/external/my-orders?page=${page}&limit=${limit}`;
        if (search) url += `&search=${encodeURIComponent(search)}`;
        const response = yield call(
            requestHandler,
            url,
            {
                method: "GET",
                headers: {
                    "Authorization": authToken,
                    "Content-Type": "application/json"
                }
            }
        );
        yield put(getMyOrdersSuccess(response));
    } catch (error) {
        yield put(getMyOrdersFailure(error?.response?.message || error));
    }
}

// Get order detail (external user)
function* getOrderDetail(action) {
    try {
        const authToken = localStorage.getItem("item");
        if (!authToken) {
            yield put(logoutRequest());
            return;
        }
        const response = yield call(
            requestHandler,
            `${process.env.REACT_APP_SERVER_API}api/external/order/${action.payload}`,
            {
                method: "GET",
                headers: {
                    "Authorization": authToken,
                    "Content-Type": "application/json"
                }
            }
        );
        yield put(getOrderDetailSuccess(response));
    } catch (error) {
        yield put(getOrderDetailFailure(error?.response?.message || error));
    }
}

// Get order report (external user)
function* getOrderReport(action) {
    try {
        const authToken = localStorage.getItem("item");
        if (!authToken) {
            yield put(logoutRequest());
            return;
        }
        const response = yield call(
            requestHandler,
            `${process.env.REACT_APP_SERVER_API}api/external/order/${action.payload}/report`,
            {
                method: "GET",
                headers: {
                    "Authorization": authToken,
                    "Content-Type": "application/json"
                }
            }
        );
        yield put(getOrderReportSuccess(response));
    } catch (error) {
        yield put(getOrderReportFailure(error?.response?.message || error));
    }
}

function* saga() {
    yield takeLatest('externalReferrer/getExternalReferrersRequest', getExternalReferrers);
    yield takeLatest('externalReferrer/getExternalReferrerRequest', getExternalReferrer);
    yield takeLatest('externalReferrer/createExternalReferrerRequest', createExternalReferrer);
    yield takeLatest('externalReferrer/updateExternalReferrerRequest', updateExternalReferrer);
    yield takeLatest('externalReferrer/deleteExternalReferrerRequest', deleteExternalReferrer);
    yield takeLatest('externalReferrer/assignOrderRequest', assignOrder);
    yield takeLatest('externalReferrer/revokeOrderRequest', revokeOrder);
    yield takeLatest('externalReferrer/getAssignedOrdersRequest', getAssignedOrders);
    yield takeLatest('externalReferrer/searchOrdersRequest', searchOrders);
    yield takeLatest('externalReferrer/getMyOrdersRequest', getMyOrders);
    yield takeLatest('externalReferrer/getOrderDetailRequest', getOrderDetail);
    yield takeLatest('externalReferrer/getOrderReportRequest', getOrderReport);
}

export default saga;
